<template>
  <div>
    <div ref="editorId" class="abc"></div>
    <div class="text-center">
      <button v-if="!viewMode && !hideSaveButton" type="button" class="btn btn-success mr-xs mt-5 mb-2" @click="getOutput">Save</button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import EditorJS from "@editorjs/editorjs";
// Temporarily disabling CustomLink to resolve error
// import CustomLink from "@/components/Editor/custom-link/index.js";
// import { resolve } from 'dns';

import Header from '@editorjs/header';
import List from '@editorjs/list';
import SimpleImage from '@editorjs/simple-image';
import InlineCode from '@editorjs/inline-code';
import Embed from '@editorjs/embed';
import Marker from '@editorjs/marker';
import Delimiter from '@editorjs/delimiter';
import Table from '@editorjs/table';
import Checklist from '@editorjs/checklist';

// Dynamic imports for problematic modules
let ImageTool = null;
let Paragraph = null;

// Remove the problematic async function and just use fallbacks

// Add a simple custom paragraph tool since @editorjs/paragraph is problematic
class CustomParagraph {
  constructor({ data, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this._data = data || {};
    this._element = null;
  }

  static get toolbox() {
    return {
      icon: '<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M13.5 1H2c-.3 0-.5.2-.5.5s.2.5.5.5h11c.3 0 .5-.2.5-.5S13.8 1 13.5 1z"/><path d="M11.5 5H2c-.3 0-.5.2-.5.5s.2.5.5.5h9.5c.3 0 .5-.2.5-.5S11.8 5 11.5 5z"/><path d="M13.5 9H2c-.3 0-.5.2-.5.5s.2.5.5.5h11c.3 0 .5-.2.5-.5S13.8 9 13.5 9z"/><path d="M9.5 13H2c-.3 0-.5.2-.5.5s.2.5.5.5h7.5c.3 0 .5-.2.5-.5S9.8 13 9.5 13z"/></svg>',
      title: 'Paragraph'
    };
  }

  static get sanitize() {
    return {
      text: {
        b: true,
        strong: true,
        i: true,
        em: true,
        u: true,
        mark: true,
        code: true,
        br: true,
        ul: true,
        ol: true,
        li: true,
        a: {
          href: true,
          target: true,
          rel: true,
          class: true,
          'data-link-name': true,
          'data-link-type': true,
          'data-real-href': true
        },
        span: {
          style: true
        },
        s: true,
        del: true,
        sup: true,
        sub: true
      }
    };
  }

  render() {
    const wrapper = document.createElement('div');
    wrapper.contentEditable = !this.readOnly;
    wrapper.innerHTML = this._data.text || '';
    wrapper.style.outline = 'none';
    wrapper.style.minHeight = '1.2em';
    wrapper.style.lineHeight = '1.6';

    // Apply text alignment if set
    const alignment = this._data.alignment;
    if (alignment && alignment !== 'left') {
      wrapper.style.textAlign = alignment;
    }

    // Store reference to wrapper for saving
    this._element = wrapper;
    
    wrapper.addEventListener('input', () => {
      this._data.text = wrapper.innerHTML;
    });
    
    // Also listen for keyup to catch all changes
    wrapper.addEventListener('keyup', () => {
      this._data.text = wrapper.innerHTML;
    });
    
    // Handle paste events - convert external HTML and plain text formatting
    wrapper.addEventListener('paste', (event) => {
      // Get HTML and plain text from clipboard
      const clipboardHtml = event.clipboardData?.getData('text/html');
      const clipboardText = event.clipboardData?.getData('text/plain');
      const hasFiles = event.clipboardData?.files && event.clipboardData.files.length > 0;
      const hasImageFile = hasFiles && Array.from(event.clipboardData.files).some(f => f.type.startsWith('image/'));
      const hasImgTag = clipboardHtml && /<img\s/i.test(clipboardHtml);

      console.log('[CustomParagraph paste]', {
        hasFiles,
        hasImageFile,
        hasImgTag,
        fileTypes: hasFiles ? Array.from(event.clipboardData.files).map(f => f.type) : [],
        clipboardHtmlLength: clipboardHtml ? clipboardHtml.length : 0,
        clipboardHtmlPreview: clipboardHtml ? clipboardHtml.substring(0, 200) : null,
        clipboardText: clipboardText ? clipboardText.substring(0, 100) : null
      });

      // If clipboard has an image file or <img> tags, let EditorJS route it to the image tool
      if (hasImageFile || hasImgTag) {
        console.log('[CustomParagraph paste] Detected image content, deferring to EditorJS image tool');
        return;
      }

      // Check if pasted content is a YouTube URL - let EditorJS embed tool handle it
      const youtubePattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
      if (clipboardText && youtubePattern.test(clipboardText.trim())) {
        // Don't prevent default - let EditorJS embed tool process it
        return;
      }

      // Check if this is plain text with bullets (common from PDFs)
      const bulletPattern = /^[\s]*[•\-\*\u2022\u2023\u25E6\u2043\u2219☐]/m;
      const hasPlainTextBullets = clipboardText && bulletPattern.test(clipboardText);

      if (hasPlainTextBullets) {
        // Handle plain text with bullet points (from PDFs, etc.)
        event.preventDefault();
        event.stopPropagation();

        let cleanedHtml = this._convertPlainTextToHtml(clipboardText);
        this._insertHtmlAtCursor(cleanedHtml, wrapper);
        this._data.text = wrapper.innerHTML;
      } else if (clipboardHtml) {
        // Handle HTML content (from Word, Google Docs, websites)
        event.preventDefault();
        event.stopPropagation();

        let cleanedHtml = this._convertExternalHtml(clipboardHtml);
        this._insertHtmlAtCursor(cleanedHtml, wrapper);
        this._data.text = wrapper.innerHTML;
      } else if (clipboardText) {
        // Plain text without bullets - convert line breaks to <br> for better formatting
        event.preventDefault();
        event.stopPropagation();

        let cleanedHtml = this._convertPlainTextToHtml(clipboardText);
        this._insertHtmlAtCursor(cleanedHtml, wrapper);
        this._data.text = wrapper.innerHTML;
      }
    });
    
    return wrapper;
  }

  // Insert HTML at cursor position
  _insertHtmlAtCursor(html, wrapper) {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      // Create a temporary container to hold the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Insert each child node
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      range.insertNode(fragment);
      
      // Move cursor to end of inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  // Convert plain text (from PDFs, etc.) to HTML with proper formatting
  _convertPlainTextToHtml(text) {
    if (!text) return '';
    
    // Split into lines (keep empty lines for now to detect groupings)
    const lines = text.split(/\r?\n/);
    let result = [];
    let bulletItems = [];
    let consecutiveEmptyLines = 0;
    
    // Bullet patterns: •, -, *, ☐ and various unicode bullets
    const bulletPattern = /^[\s]*([•\-\*\u2022\u2023\u25E6\u2043\u2219☐])\s*/;
    // Numbered list pattern: 1., 2., etc.
    const numberedPattern = /^[\s]*(\d+)\.\s+/;
    
    // First pass: identify if we have bullets mixed with content
    const hasBullets = lines.some(line => bulletPattern.test(line.trim()));
    
    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push('<ul>' + bulletItems.map(item => `<li>${item}</li>`).join('') + '</ul>');
        bulletItems = [];
      }
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Track empty lines
      if (!trimmedLine) {
        consecutiveEmptyLines++;
        // Only close bullet list after 2+ consecutive empty lines (paragraph break)
        // Single empty lines between bullets are ignored
        if (consecutiveEmptyLines >= 2) {
          flushBullets();
          result.push('<br><br>');
        }
        continue;
      }
      
      // Reset empty line counter when we hit content
      consecutiveEmptyLines = 0;
      
      // Check for bullet point
      const bulletMatch = trimmedLine.match(bulletPattern);
      if (bulletMatch) {
        const content = trimmedLine.replace(bulletPattern, '').trim();
        bulletItems.push(content);
        continue;
      }
      
      // Check for numbered item
      const numberedMatch = trimmedLine.match(numberedPattern);
      if (numberedMatch) {
        // Flush any pending bullets before numbered item
        flushBullets();
        // Add numbered item as bold text
        result.push(`<strong>${trimmedLine}</strong>`);
        continue;
      }
      
      // Regular text line - flush bullets first
      flushBullets();
      result.push(trimmedLine);
    }
    
    // Flush any remaining bullets
    flushBullets();
    
    // Join with line breaks, clean up excessive breaks
    let html = result.join('<br>');
    html = html.replace(/<\/ul><br>/g, '</ul>');
    html = html.replace(/<br><ul>/g, '<ul>');
    html = html.replace(/(<br>){3,}/g, '<br><br>');
    
    return html;
  }

  // Convert external HTML (Word, Google Docs, web pages) to semantic tags
  _convertExternalHtml(html) {
    // Create a temporary container to work with the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remove all style tags and script tags
    tempDiv.querySelectorAll('style, script, meta, link').forEach(el => el.remove());
    
    // Remove Word/Office-specific tags and comments
    tempDiv.innerHTML = tempDiv.innerHTML
      .replace(/<!--\[if[\s\S]*?endif\]-->/gi, '')
      .replace(/<!\[[\s\S]*?\]>/gi, '')
      .replace(/<o:p[\s\S]*?<\/o:p>/gi, '')
      .replace(/<w:[\s\S]*?<\/w:[\s\S]*?>/gi, '');
    
    // Convert spans with inline styles to semantic tags
    const allElements = tempDiv.querySelectorAll('*');
    allElements.forEach(el => {
      const style = el.style;
      const computedStyle = el.getAttribute('style') || '';
      
      // Check for bold styling
      const isBold = style.fontWeight === 'bold' || 
                     style.fontWeight === '700' ||
                     parseInt(style.fontWeight) >= 600 ||
                     computedStyle.includes('font-weight: bold') ||
                     computedStyle.includes('font-weight:bold') ||
                     computedStyle.includes('font-weight: 700') ||
                     computedStyle.includes('font-weight:700');
      
      // Check for italic styling
      const isItalic = style.fontStyle === 'italic' ||
                       computedStyle.includes('font-style: italic') ||
                       computedStyle.includes('font-style:italic');
      
      // Check for underline styling
      const isUnderline = style.textDecoration?.includes('underline') ||
                          computedStyle.includes('text-decoration: underline') ||
                          computedStyle.includes('text-decoration:underline') ||
                          computedStyle.includes('underline');
      
      // Get the inner content
      let content = el.innerHTML;
      
      // Wrap with appropriate semantic tags (innermost first)
      if (isUnderline) {
        content = `<u>${content}</u>`;
      }
      if (isItalic) {
        content = `<em>${content}</em>`;
      }
      if (isBold) {
        content = `<strong>${content}</strong>`;
      }
      
      // If we made any conversions on a span/div, replace with the content
      if ((isBold || isItalic || isUnderline) && 
          (el.tagName === 'SPAN' || el.tagName === 'DIV' || el.tagName === 'FONT')) {
        el.outerHTML = content;
      }
    });
    
    // Convert remaining tags to plain text or allowed tags
    let result = tempDiv.innerHTML;
    
    // Convert <b> to <strong> for consistency
    result = result.replace(/<b\b[^>]*>/gi, '<strong>').replace(/<\/b>/gi, '</strong>');
    
    // Convert <i> to <em> for consistency
    result = result.replace(/<i\b[^>]*>/gi, '<em>').replace(/<\/i>/gi, '</em>');
    
    // Remove spans without meaningful content conversion
    result = result.replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, '$1');
    
    // Convert divs and paragraphs to line breaks (for single-block paste)
    result = result.replace(/<\/?(div|p)[^>]*>/gi, ' ');
    
    // Clean up multiple spaces and normalize
    result = result.replace(/\s+/g, ' ').trim();
    
    // Remove any remaining tags that aren't in our allowed list
    // Keep: strong, em, b, i, u, mark, code, a, br, ul, ol, li
    const allowedTags = ['strong', 'em', 'b', 'i', 'u', 'mark', 'code', 'a', 'br', 'ul', 'ol', 'li'];
    const tagRegex = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
    result = result.replace(tagRegex, (match, tagName) => {
      if (allowedTags.includes(tagName.toLowerCase())) {
        return match;
      }
      return '';
    });
    
    return result;
  }

  save(blockContent) {
    // Get the current HTML content from the DOM element
    const currentContent = this._element ? this._element.innerHTML : (this._data.text || '');

    const result = { text: currentContent };

    // Persist alignment if set (omit 'left' to keep data lean for existing articles)
    if (this._data.alignment && this._data.alignment !== 'left') {
      result.alignment = this._data.alignment;
    }

    return result;
  }

  static get isReadOnlySupported() {
    return true;
  }
}

// Base PLUGINS object with working tools
const PLUGINS = {
  header: Header,
  list: List,
  paragraph: CustomParagraph,
  inlineCode: InlineCode,
  embed: Embed,
  marker: Marker,
  delimiter: Delimiter,
  table: Table,
  checklist: Checklist,
  // Enhanced custom image tool
  image: class EditorImageTool {
    constructor({ data, config, api, readOnly }) {
      this.api = api;
      this.readOnly = readOnly;
      this.config = config || {};
      this._data = data || {};
      this._element = null;
    }

    static get toolbox() {
      return {
        icon: '<svg width="17" height="15" viewBox="0 0 336 276" xmlns="http://www.w3.org/2000/svg"><path d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 66-39 32 40zm0 52l-43-53-49 30-24-24-59 47V220c0 19 15 34 34 34H257c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>',
        title: 'Image'
      };
    }

    static get isReadOnlySupported() {
      return true;
    }

    // Enable paste handling: Ctrl+V images, <img> tags from pasted HTML, and image URLs
    static get pasteConfig() {
      return {
        tags: [{ img: { src: true } }],
        files: {
          mimeTypes: ['image/*']
        },
        patterns: {
          image: /https?:\/\/\S+\.(gif|jpe?g|tiff|png|svg|webp)(\?\S*)?$/i
        }
      };
    }

    async onPaste(event) {
      switch (event.type) {
        case 'tag': {
          // Pasted HTML containing <img> tags (e.g. from Scribe)
          const img = event.detail.data;
          const src = img.src;
          if (!src) return;

          this._showLoading('Uploading pasted image...');

          try {
            if (this.config.uploader && this.config.uploader.uploadByUrl) {
              const response = await this.config.uploader.uploadByUrl(src);
              if (response.success === 1) {
                this._data = { file: { url: response.file.url, name: response.file.name }, caption: img.alt || '', withBorder: false, withBackground: false, stretched: false };
              }
            } else {
              this._data = { file: { url: src }, caption: img.alt || '', withBorder: false, withBackground: false, stretched: false };
            }
          } catch (err) {
            console.warn('Image paste tag upload failed, using external URL:', err);
            this._data = { file: { url: src }, caption: img.alt || '', withBorder: false, withBackground: false, stretched: false };
          }

          this._rerenderAfterPaste();
          break;
        }
        case 'file': {
          // Ctrl+V with an image copied to clipboard
          const file = event.detail.file;
          if (!file) return;

          this._showLoading('Uploading pasted image...');

          try {
            if (this.config.uploader && this.config.uploader.uploadByFile) {
              const response = await this.config.uploader.uploadByFile(file);
              if (response.success === 1) {
                this._data = { file: { url: response.file.url, name: response.file.name }, caption: '', withBorder: false, withBackground: false, stretched: false };
              }
            }
          } catch (err) {
            console.error('Image paste file upload failed:', err);
          }

          this._rerenderAfterPaste();
          break;
        }
        case 'pattern': {
          // Plain-text URL pasted that matches an image extension
          const url = event.detail.data;
          if (!url) return;

          this._showLoading('Uploading image from URL...');

          try {
            if (this.config.uploader && this.config.uploader.uploadByUrl) {
              const response = await this.config.uploader.uploadByUrl(url);
              if (response.success === 1) {
                this._data = { file: { url: response.file.url }, caption: '', withBorder: false, withBackground: false, stretched: false };
              }
            } else {
              this._data = { file: { url: url }, caption: '', withBorder: false, withBackground: false, stretched: false };
            }
          } catch (err) {
            console.warn('Image paste pattern upload failed, using URL directly:', err);
            this._data = { file: { url: url }, caption: '', withBorder: false, withBackground: false, stretched: false };
          }

          this._rerenderAfterPaste();
          break;
        }
      }
    }

    _showLoading(message) {
      if (this._element) {
        this._element.innerHTML = `<div style="text-align:center;padding:20px;color:#6c757d;">${message}</div>`;
      }
    }

    _rerenderAfterPaste() {
      if (!this._element) return;
      this._element.innerHTML = '';
      if (this._data.file && this._data.file.url) {
        this._renderImage(this._element);
      } else {
        this._renderUploadInterface(this._element);
      }
    }

    render() {
      const wrapper = document.createElement('div');
      wrapper.classList.add('image-tool');
      this._element = wrapper; // Store reference for onPaste re-rendering

      // If we have existing data, show the image
      if (this._data && this._data.file && this._data.file.url) {
        this._renderImage(wrapper);
        return wrapper;
      }

      // Otherwise show upload interface
      this._renderUploadInterface(wrapper);
      return wrapper;
    }

    _renderImage(wrapper) {
      const imageContainer = document.createElement('div');
      imageContainer.classList.add('image-tool__image');

      const img = document.createElement('img');
      img.src = this._data.file.url;
      img.alt = this._data.caption || '';
      
      if (this._data.stretched) {
        img.classList.add('image-tool__image--stretched');
      }
      
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '8px';
      
      imageContainer.appendChild(img);

      // Add caption if exists
      if (this._data.caption) {
        const caption = document.createElement('div');
        caption.classList.add('image-tool__caption');
        caption.textContent = this._data.caption;
        caption.style.fontSize = '13px';
        caption.style.color = '#707684';
        caption.style.textAlign = 'center';
        caption.style.marginTop = '8px';
        imageContainer.appendChild(caption);
      }

      wrapper.appendChild(imageContainer);
    }

    _renderUploadInterface(wrapper) {
      wrapper.innerHTML = `
        <div class="image-tool__upload" style="
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 2px dashed #dee2e6;
          border-radius: 12px;
          padding: 40px 20px;
          text-align: center;
          transition: all 0.3s ease;
          min-height: 200px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 20px;
          position: relative;
          overflow: hidden;
        ">
          <div class="upload-icon" style="
            width: 56px;
            height: 56px;
            background: rgba(58, 191, 148, 0.1);
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;
          ">
            <svg width="26" height="26" fill="#3abf94" viewBox="0 0 24 24">
              <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z" />
            </svg>
          </div>
          
          <div class="upload-text" style="
            color: #495057;
            font-size: 16px;
            font-weight: 500;
            margin-bottom: 8px;
          ">
            Drop an image here or click to upload
          </div>
          
          <div class="upload-buttons" style="
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            justify-content: center;
          ">
            <button type="button" class="upload-btn" style="
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 10px 20px;
              background: #3abf94;
              color: white;
              border: none;
              border-radius: 8px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              font-size: 14px;
              box-shadow: 0 1px 3px rgba(58, 191, 148, 0.3);
            ">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
              Upload File
            </button>
            
            <button type="button" class="url-btn" style="
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 10px 20px;
              background: white;
              color: #3abf94;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              font-size: 14px;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            ">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M6.354 5.5H4a3 3 0 0 0 0 6h3a3 3 0 0 0 2.83-4H9c-.086 0-.17.01-.25.031A2 2 0 0 1 7 10.5H4a2 2 0 1 1 0-4h1.535c.218-.376.495-.714.82-1z"/>
                <path d="M9 5.5a3 3 0 0 0-2.83 4h1.098A2 2 0 0 1 9 6.5h3a2 2 0 1 1 0 4h-1.535a4.02 4.02 0 0 1-.82 1H12a3 3 0 1 0 0-6H9z"/>
              </svg>
              Add URL
            </button>
          </div>
          
          <input type="file" class="file-input" accept="image/*" style="display: none;">
          
          <div class="url-input-container" style="
            display: none;
            width: 100%;
            max-width: 400px;
            margin-top: 10px;
          ">
            <input type="url" class="url-input" placeholder="Paste image URL here..." style="
              width: 100%;
              padding: 12px 16px;
              border: 2px solid #dee2e6;
              border-radius: 8px;
              font-size: 14px;
              outline: none;
              transition: border-color 0.2s ease;
            ">
            <div style="
              display: flex;
              gap: 8px;
              margin-top: 12px;
              justify-content: center;
            ">
              <button type="button" class="confirm-url-btn" style="
                padding: 8px 16px;
                background: #28a745;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 13px;
              ">Add</button>
              <button type="button" class="cancel-url-btn" style="
                padding: 8px 16px;
                background: #6c757d;
                color: white;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                font-size: 13px;
              ">Cancel</button>
            </div>
          </div>
          
          <div class="loading-indicator" style="
            display: none;
            color: #007bff;
            font-size: 14px;
            font-weight: 500;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <div style="
                width: 20px;
                height: 20px;
                border: 2px solid #e9ecef;
                border-top: 2px solid #007bff;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              "></div>
              Uploading...
            </div>
          </div>
        </div>
      `;

      this._bindUploadEvents(wrapper);
    }

    _bindUploadEvents(wrapper) {
      const uploadArea = wrapper.querySelector('.image-tool__upload');
      const fileInput = wrapper.querySelector('.file-input');
      const uploadBtn = wrapper.querySelector('.upload-btn');
      const urlBtn = wrapper.querySelector('.url-btn');
      const urlContainer = wrapper.querySelector('.url-input-container');
      const urlInput = wrapper.querySelector('.url-input');
      const confirmUrlBtn = wrapper.querySelector('.confirm-url-btn');
      const cancelUrlBtn = wrapper.querySelector('.cancel-url-btn');
      const loadingIndicator = wrapper.querySelector('.loading-indicator');

      // File upload handlers
      uploadBtn.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', (e) => this._handleFileUpload(e, wrapper, loadingIndicator));

      // URL input handlers
      urlBtn.addEventListener('click', () => {
        urlContainer.style.display = 'block';
        urlBtn.style.display = 'none';
        uploadBtn.style.display = 'none';
        urlInput.focus();
      });

      confirmUrlBtn.addEventListener('click', () => {
        const url = urlInput.value.trim();
        if (url) {
          this._handleUrlInput(url, wrapper);
        }
      });

      cancelUrlBtn.addEventListener('click', () => {
        urlContainer.style.display = 'none';
        urlBtn.style.display = 'inline-flex';
        uploadBtn.style.display = 'inline-flex';
        urlInput.value = '';
      });

      urlInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          const url = urlInput.value.trim();
          if (url) {
            this._handleUrlInput(url, wrapper);
          }
        }
      });

      // Drag and drop handlers
      uploadArea.addEventListener('dragover', (e) => {
        e.preventDefault();
        uploadArea.style.borderColor = '#28a745';
        uploadArea.style.background = 'linear-gradient(135deg, #f0fff4, #d4edda)';
      });

      uploadArea.addEventListener('dragleave', (e) => {
        e.preventDefault();
        uploadArea.style.borderColor = '#dee2e6';
        uploadArea.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
      });

      uploadArea.addEventListener('drop', (e) => {
        e.preventDefault();
        uploadArea.style.borderColor = '#dee2e6';
        uploadArea.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
        
        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].type.startsWith('image/')) {
          this._handleFileUpload({ target: { files } }, wrapper, loadingIndicator);
        }
      });
    }

    async _handleFileUpload(event, wrapper, loadingIndicator) {
      const file = event.target.files[0];
      if (!file) return;

      // Show loading indicator
      wrapper.querySelector('.upload-buttons').style.display = 'none';
      loadingIndicator.style.display = 'block';

      try {
        // Use the uploader configuration from WikiEdit.vue
        const response = await this.config.uploader.uploadByFile(file);
        
        this._data = {
          file: {
            url: response.file.url
          },
          caption: '',
          withBorder: false,
          withBackground: false,
          stretched: false
        };
        
        // Hide loading indicator before clearing wrapper
        loadingIndicator.style.display = 'none';
        
        // Clear wrapper and render image
        wrapper.innerHTML = '';
        this._renderImage(wrapper);
        
        // Add success message briefly
        setTimeout(() => {
          const successMsg = document.createElement('div');
          successMsg.style.position = 'absolute';
          successMsg.style.top = '10px';
          successMsg.style.right = '10px';
          successMsg.style.background = '#28a745';
          successMsg.style.color = 'white';
          successMsg.style.padding = '5px 10px';
          successMsg.style.borderRadius = '4px';
          successMsg.style.fontSize = '12px';
          successMsg.textContent = '✓ Uploaded';
          successMsg.style.zIndex = '1000';
          if (wrapper.querySelector('.image-tool__image')) {
            wrapper.querySelector('.image-tool__image').style.position = 'relative';
            wrapper.querySelector('.image-tool__image').appendChild(successMsg);
            setTimeout(() => successMsg.remove(), 2000);
          }
        }, 100);
        
      } catch (error) {
        // Hide loading indicator on error
        loadingIndicator.style.display = 'none';
        wrapper.querySelector('.upload-buttons').style.display = 'flex';
        
        // Show error message
        const errorMsg = document.createElement('div');
        errorMsg.style.color = '#dc3545';
        errorMsg.style.fontSize = '14px';
        errorMsg.style.marginTop = '10px';
        errorMsg.textContent = 'Upload failed. Please try again.';
        wrapper.querySelector('.image-tool__upload').appendChild(errorMsg);
        setTimeout(() => errorMsg.remove(), 3000);
      }
    }

    _handleUrlInput(url, wrapper) {
      if (!this._isValidUrl(url)) {
        alert('Please enter a valid image URL');
        return;
      }

      this._data = {
        file: { url },
        caption: '',
        withBorder: false,
        withBackground: false,
        stretched: false
      };
      
      wrapper.innerHTML = '';
      this._renderImage(wrapper);
    }

    _isValidUrl(string) {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    }

    save() {
      return this._data;
    }

    static get sanitize() {
      return {
        url: {},
        caption: {
          br: true,
        },
      };
    }
  }
};



export default {
  name: "Editor",
  data() {
    return {
      editor: null
    };
  },

  props: {
    editorData: Object,
    editorTools: Object,
    viewMode: Boolean,
    hideSaveButton: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    prepareEditorTools() {
      try {
        if (!this.editorTools) {
          return {};
        }

        const toolsCopy = {};
        
        Object.keys(this.editorTools).forEach(tool => {
          try {
            // Handle tools from PLUGINS object
            if (PLUGINS[tool]) {
              toolsCopy[tool] = { ...this.editorTools[tool] };
              toolsCopy[tool].class = PLUGINS[tool];
            }
            // Handle tools that already have a class (like wikiLink)
            else if (this.editorTools[tool] && this.editorTools[tool].class) {
              toolsCopy[tool] = { ...this.editorTools[tool] };
            }
            // Skip tools not found in PLUGINS and without class defined
          } catch (error) {
            console.error(`Error configuring tool '${tool}':`, error);
          }
        });

        return toolsCopy;
      } catch (error) {
        console.error('Error in prepareEditorTools:', error);
        return {};
      }
    },

    getOutput() {
      if (!this.editor) {
        return;
      }

      this.editor.save().then((outputData) => {
        this.$emit('save', outputData);
      }).catch((error) => {
        console.error('Saving failed:', error);
      });
    }
  },

  mounted() {
    /* eslint-disable */
    try {
      const preparedTools = this.prepareEditorTools();
      
      this.editor = new EditorJS({
        readOnly: this.viewMode,
        holder: this.$refs.editorId,
        autofocus: true,
        logLevel: 'ERROR',
        data: this.editorData,
        tools: preparedTools,
        onChange: (_, event) => {
          try {
            this.$emit('onChange', event);
          } catch (error) {
            console.error('Error in onChange handler:', error);
          }
        },
        onReady: () => {
          try {
            this.$emit('onReady');
          } catch (error) {
            console.error('Error in onReady handler:', error);
          }
        }
      });
    } catch (error) {
      console.error('Error initializing Editor:', error);
    }
  }
};
</script>

<style scoped>
.editor-wrapper {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  min-height: 300px;
}

/* Use deep selectors for Editor.js elements that need styling */
.editor-wrapper ::v-deep .image-tool__upload {
  border: 2px dashed #e1e8ed;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.editor-wrapper ::v-deep .image-tool__upload:hover {
  border-color: #1976d2;
  background: #f3f8ff;
}

.editor-wrapper ::v-deep .image-tool__upload--dragover {
  border-color: #1976d2;
  background: #e3f2fd;
  transform: scale(1.02);
}

.editor-wrapper ::v-deep .image-tool__upload-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  transition: background-color 0.2s;
}

.editor-wrapper ::v-deep .image-tool__upload-button:hover {
  background: #1565c0;
}

.editor-wrapper ::v-deep .image-tool__url-input {
  display: flex;
  gap: 8px;
  max-width: 400px;
  margin: 0 auto;
}

.editor-wrapper ::v-deep .image-tool__url-input input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.editor-wrapper ::v-deep .image-tool__url-input button {
  padding: 10px 16px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.editor-wrapper ::v-deep .image-tool__url-input button:hover {
  background: #45a049;
}

.editor-wrapper ::v-deep .image-tool__image {
  text-align: center;
  margin: 10px 0;
}

.editor-wrapper ::v-deep .image-tool__image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.editor-wrapper ::v-deep .image-tool__image--stretched img {
  width: 100%;
}

.editor-wrapper ::v-deep .image-tool__image--bordered img {
  border: 2px solid #e1e8ed;
}

.editor-wrapper ::v-deep .image-tool__caption {
  margin-top: 10px;
  font-style: italic;
  color: #666;
  text-align: center;
  font-size: 14px;
}

.editor-wrapper ::v-deep .image-tool__loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.editor-wrapper ::v-deep .image-tool__error {
  margin: 20px 0;
}
</style>
